import { DatePicker, Form, Input, Select } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { FormModal } from "@/shared/components/FormModal";
import { useCreateCorrection } from "../hooks/useAttendance";
import type { AttendanceEvent, CorrectionInput } from "../types";

interface CorrectionFormValues {
	type: CorrectionInput["type"];
	occurredAt: Dayjs;
	reason: string;
	correctedEventId?: string;
}

interface CorrectionModalProps {
	dayId: string | null;
	events: AttendanceEvent[];
	open: boolean;
	onClose: () => void;
}

/**
 * ⚠️ **REJADAN CHEKINISH.** Reja xom `<Modal>` + `form.validateFields()` +
 * `mutateAsync()` yozgan edi va `onOk` ni `async` qilgan edi. Ikkala `await`
 * ham REJECT bo'ladi (validatsiya xatosi va HTTP xatosi), `Modal.onOk` esa
 * qaytgan promise'ni kutmaydi — natijada har xato «unhandled rejection»
 * bo'lardi. Loyihaning o'z naqshi (`FormModal` + `onFinish`) bu bo'shliqni
 * butunlay yopadi: validatsiya rc-field-form ichida qoladi, mutatsiya esa
 * `mutate()` bilan chaqiriladi va xato `useCreateCorrection.onError` da
 * ko'rsatiladi.
 *
 * Forma faqat MUVAFFAQIYATDAN keyin tozalanadi: `FormModal` `resetFields()`
 * ni `open` bo'lganda qiladi, ya'ni xatoda oyna ochiq qoladi va kiritilgan
 * qiymatlar saqlanadi (1c da tuzatilgan regressiya sinfi).
 */
export function CorrectionModal({ dayId, events, open, onClose }: CorrectionModalProps) {
	const { t } = useTranslation("attendance");
	const mutation = useCreateCorrection(dayId);

	const typeOptions = [
		{ value: "check_in", label: t("correction.types.check_in") },
		{ value: "check_out", label: t("correction.types.check_out") },
		{ value: "departure", label: t("correction.types.departure") },
		{ value: "return", label: t("correction.types.return") },
		{ value: "correction", label: t("correction.types.correction") },
	];

	const handleSubmit = (values: CorrectionFormValues) => {
		mutation.mutate(
			{
				type: values.type,
				occurredAt: values.occurredAt.toISOString(),
				reason: values.reason,
				correctedEventId: values.correctedEventId,
			},
			{ onSuccess: onClose }
		);
	};

	return (
		<FormModal<CorrectionFormValues>
			open={open}
			title={t("correction.title")}
			isSubmitting={mutation.isPending}
			onCancel={onClose}
			onSubmit={handleSubmit}
		>
			<Form.Item
				name="type"
				label={t("correction.typeLabel")}
				rules={[{ required: true, message: t("correction.typeRequired") }]}
			>
				<Select options={typeOptions} />
			</Form.Item>
			<Form.Item
				name="occurredAt"
				label={t("correction.timeLabel")}
				rules={[{ required: true, message: t("correction.timeRequired") }]}
			>
				<DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: "100%" }} />
			</Form.Item>
			<Form.Item name="correctedEventId" label={t("correction.correctedEventLabel")}>
				<Select
					allowClear
					placeholder={t("correction.correctedEventPlaceholder")}
					options={events.map((event) => ({
						value: event.id,
						label: `${dayjs(event.occurredAt).format("HH:mm")} · ${event.type}`,
					}))}
				/>
			</Form.Item>
			{/* ⚠️ Sabab MAJBURIY — backend ham uni talab qiladi
			    (`messages.attendance.correctionReasonRequired`). Bu yerdagi
			    qoida faqat qulaylik; haqiqiy qorovul serverda. */}
			<Form.Item
				name="reason"
				label={t("correction.reasonLabel")}
				rules={[{ required: true, message: t("correction.reasonRequired") }]}
			>
				<Input.TextArea rows={3} maxLength={500} showCount />
			</Form.Item>
		</FormModal>
	);
}
